import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Plus, Mail, Trash2, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import UserPermissionsPanel from "@/components/UserPermissionsPanel";

export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [sending, setSending] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const load = async () => {
    try {
      const [u, i] = await Promise.all([api.get("/admin/users"), api.get("/admin/invitations")]);
      setUsers(u.data);
      setInvites(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/admin/invitations", { email, role });
      toast.success(`Invitation envoyée à ${email}.`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const cancel = async (id) => {
    await api.delete(`/admin/invitations/${id}`);
    load();
  };

  return (
    <>
      <div className="space-y-8">
        {/* Invite form */}
        <form onSubmit={invite} className="border border-zinc-200 bg-white p-6" data-testid="invite-form">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-4">
            <Mail size={12} strokeWidth={1.5} /> Inviter un utilisateur
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              data-testid="invite-email-input"
              type="email" required maxLength={200}
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemple.fr"
              className="flex-1 border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
            />
            <select
              data-testid="invite-role-select"
              value={role} onChange={(e) => setRole(e.target.value)}
              className="border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
            >
              <option value="user">Utilisateur</option>
              <option value="admin">Administrateur</option>
            </select>
            <button
              data-testid="invite-submit"
              type="submit" disabled={sending}
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-5 py-2 transition-colors disabled:opacity-50"
            >
              <Plus size={14} strokeWidth={1.5} />
              {sending ? "Envoi..." : "Inviter"}
            </button>
          </div>
          <div className="mt-3 text-xs text-zinc-500 font-mono">
            L'utilisateur recevra un e-mail d'invitation (lien valable 7 jours).
          </div>
        </form>

        {/* Users */}
        <div className="border border-zinc-200 bg-white" data-testid="users-list">
          <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50/50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            Comptes actifs · {users.length}
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/50">
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Nom</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Email</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Rôle</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">E-mail vérifié</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Inscription</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-6 text-sm text-zinc-500">Chargement…</td></tr>
              ) : users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  onClick={() => setSelectedUser(u)}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer"
                >
                  <td className="py-3 px-4 text-sm font-medium">{u.name || "—"}</td>
                  <td className="py-3 px-4 text-sm font-mono text-zinc-600">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-mono px-2 py-1 border ${
                      u.role === "admin" ? "text-[#002FA7] border-[#002FA7]/40 bg-[#002FA7]/5" : "text-zinc-500 border-zinc-200"
                    }`}>{u.role === "admin" ? "Admin" : "User"}</span>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {u.email_verified
                      ? <span className="inline-flex items-center gap-1 text-[#16A34A]"><UserCheck size={14} /> vérifié</span>
                      : <span className="inline-flex items-center gap-1 text-[#FF3B30]"><UserX size={14} /> en attente</span>}
                  </td>
                  <td className="py-3 px-4 text-xs font-mono text-zinc-500">
                    {new Date(u.created_at).toLocaleDateString("fr-FR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Invitations */}
        <div className="border border-zinc-200 bg-white" data-testid="invitations-list">
          <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50/50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            Invitations · {invites.length}
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/50">
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Email</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Rôle</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Statut</th>
                <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Expire</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-sm text-zinc-500">Aucune invitation.</td></tr>
              ) : invites.map((i) => {
                const expired = new Date(i.expires_at) < new Date();
                const status = i.accepted ? "acceptée" : expired ? "expirée" : "en attente";
                const color = i.accepted ? "#16A34A" : expired ? "#DC2626" : "#EAB308";
                return (
                  <tr key={i.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                    <td className="py-3 px-4 text-sm font-mono text-zinc-700">{i.email}</td>
                    <td className="py-3 px-4 text-sm">{i.role}</td>
                    <td className="py-3 px-4">
                      <span className="inline-block text-[10px] uppercase tracking-wider font-mono px-2 py-1 border"
                            style={{ color, borderColor: color + "40", backgroundColor: color + "10" }}>
                        {status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs font-mono text-zinc-500">
                      {new Date(i.expires_at).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {!i.accepted && (
                        <button onClick={() => cancel(i.id)} className="text-zinc-400 hover:text-[#DC2626]" data-testid={`invite-cancel-${i.id}`}>
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Side panel permissions */}
      {selectedUser && (
        <UserPermissionsPanel
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSaved={() => { load(); }}
        />
      )}
    </>
  );
}
